<template>
	<div>
		<div class="grid three">
			<a href @click.prevent="setOption('legitimate')" class="data">
				<div class="title"><span class="fa fa-hand-spock"></span>Legit</div>
				<div class="number big">{{ legitimate.length }}</div>
			</a>
			<a href @click.prevent="setOption('suspicious')" class="data">
				<div class="title"><span class="fa fa-alien-monster"></span>Suspicious</div>
				<div class="number big">{{ suspicious.length }}</div>
			</a>
			<a href @click.prevent="setOption('spam')" class="data">
				<div class="title"><span class="fa fa-comet"></span>Spam</div>
				<div class="number big">{{ (spam.length + suspicious.length) >= 100 ? '99+' : spam.length }}</div>
			</a>
		</div>

		<div class="title-container">
			<div class="title">{{ selectedOption }}</div>
		</div>
		<div class="table-scroll-wrapper">
			<div class="table list">
				<div class="row" v-if="!loading && filtered.length === 0">
					<div class="cell align-center">There are no inquiries available in this category</div>
				</div>
				<div class="row" v-if="loading">
					<div class="cell align-center">Loading...</div>
				</div>
				<div v-if="filtered.length > 0" class="row">
					<div class="cell">Actions</div>
					<div class="cell">Received</div>
					<div class="cell">From</div>
					<div class="cell">Phone Number</div>
				</div>
				<div v-for="inquiry in filtered" :key="`inquiry-${ inquiry.id }`" class="row">
					<div v-if="!loadingById[ inquiry.id ]" class="cell buttons">
						<a v-if="inquiry.validity_uncertain || !inquiry.successful" href @click.prevent="markLegitimate(inquiry)" class="button"><div class="hover-title">Mark Legitimate</div><span class="fa fa-thumbs-up"></span></a>
						<a v-if="inquiry.successful || inquiry.validity_uncertain" href @click.prevent="markSpam(inquiry)" class="button"><div class="hover-title">Mark Spam</div><span class="fa fa-thumbs-down"></span></a>
						<router-link :to="`/inquiries/${ inquiry.id }/details`" class="button"><div class="hover-title">View</div><span class="fa fa-search"></span></router-link>
					</div>
					<div v-if="loadingById[ inquiry.id ]" class="cell buttons">
						<a class="button"><span class="fa fa-sync fa-spin"></span></a>
					</div>
					<div class="cell">{{ inquiry.date }}</div>
					<div class="cell">{{ (inquiry.from || '').slice(0,40) }}</div>
					<div class="cell">{{ (inquiry.phone || '').slice(0,20) }}</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>

import moment from 'moment-timezone';
import _ from 'underscore';

export default {

	name: 'InquiriesOverviewPage',

	data() {
		return {
			loading: true,
			loadingById: {},
			inquiries: [],
			form: {
				options: 'legitimate',
			},
		};
	},

	computed: {

		selectedOption() {
			const titles = {
				legitimate: 'Legitimate Inquiries (All Time)',
				spam: 'Spam Inquiries (Past 30 Days)',
				suspicious: 'Suspicious Inquiries (Past 30 Days)',
			};

			return titles[ this.form.options ] || 'Inquiries';
		},

		filtered() {
			return this[ this.form.options ] || this.legitimate;
		},


		legitimate() {
			return this.cleanInquiries(this.inquiries.filter(inquiry => !!inquiry.successful && !inquiry.validity_uncertain));
		},

		suspicious() {
			return this.cleanInquiries(this.inquiries.filter(inquiry => inquiry.validity_uncertain));
		},

		spam() {
			return this.cleanInquiries(this.inquiries.filter(inquiry => !inquiry.successful && !inquiry.validity_uncertain));
		},

	},

	mounted() {

		if (!this.$route.query.filter) {
			this.$router.replace({ path: 'inquiries', query: { filter: 'legitimate' } });
		} else {
			this.form.options = this.$route.query.filter;
		}

		this.loadInquiries();
	},

	watch: {

		'$route.query.filter': function(newValue) {
			this.form.options = newValue;
		},

		'form.options': function(newValue) {
			if (this.$route.query.filter != newValue) {
				this.$router.push({ path: 'inquiries', query: { filter: newValue } });
			}

			if (typeof this[ newValue ] === 'undefined') {
				this.form.options = 'legitimate';
			}
		},

	},

	methods: {

		setOption(value) {
			this.$set(this.form, 'options', value);
		},

		cleanInquiries(inquiries) {
			return inquiries.map(inquiry => {
				let data = _.omit(inquiry.data, ['_token', 'subject']);

				return {
					...inquiry,
					date: moment.tz(inquiry.created_at, 'UTC').tz(moment.tz.guess()).format('MMMM DD, YYYY'),
					from: data.name || 'Unknown',
					email: data.email || 'Unknown',
					phone: data.phone || 'Unknown',
					recaptcha_score: data.recaptcha_score || false,
					recaptcha_errors: data.recaptcha_errors || false,
				};
			});
		},

		markLegitimate(inquiry) {
			this.updateInquiry({
				successful: true,
				validity_uncertain: false,
			}, inquiry);
		},

		markSpam(inquiry) {
			this.updateInquiry({
				successful: false,
				validity_uncertain: false,
			}, inquiry);
		},

		async updateInquiry(newInquiryData, inquiry) {
			this.loadingById[ inquiry.id ] = true;
			try {
				const { data } = await this.$api.post(`/inquiries/${inquiry.id}`, newInquiryData);

				const index = _.findIndex(this.inquiries, { id: inquiry.id });
				if (index !== -1) {
					this.$set(this.inquiries, index, data);
				}

				this.$store.dispatch('addToast', `Inquiry Status ${data.successful === inquiry.successful ? 'Confirmed' : 'Updated'}`);
			} catch(e) {}

			this.loadingById[ inquiry.id ] = false;
		},

		async loadInquiries() {
			this.loading = true;

			try {
				const { data } = await this.$api.get('/inquiries');

				this.inquiries = data;
			} catch (e) {
				this.$store.dispatch('errorToast', 'Unable to load inquiries. Please try again later or contact us.');
				this.inquiries = [];
			}

			this.loading = false;
		},

		inquiryStatus(inquiry) {
			if (inquiry.validity_uncertain) {
				return 'Suspicious';
			}

			return inquiry.successful ? 'Legitimate' : 'Spam';
		},

		inquiryColor(inquiry) {
			if (inquiry.validity_uncertain) {
				return 'neutral';
			}

			return inquiry.successful ? 'good-bg' : 'bad-bg';
		},

	},
};

</script>
